import personalImage from '../assets/personal_img.png';
import aboutImage from '../assets/about_img.jpg';
import cannacardLogo from '../assets/projects/cannacard.jpg';
import mindhueLogo from '../assets/projects/mindhue.jpg';
import traigaLogo from '../assets/projects/traiga.png';
import oldGloryPayLogo from '../assets/projects/oldglorypay.jpg';
import taxiLinkLogo from '../assets/projects/taxilink.png';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  to: string;
}

export interface SocialLinks {
  github: string;
  linkedin: string;
  email: string;
}

export type Platform = 'ios' | 'android' | 'web';

/** Which stylized mock screen the project card draws inside its device frame. */
export type PreviewKind = 'wallet' | 'board' | 'map' | 'voice' | 'dashboard' | 'editor' | 'booking';

export interface ProjectItem {
  id: number;
  title: string;
  category: 'mobile' | 'web';
  platforms: Platform[];
  featured?: boolean;
  preview: PreviewKind;
  tagline: string;
  description: string;
  highlights: string[];
  tech: string[];
  color: string;
  status: 'Production' | 'Live' | 'In Progress';
  github: string | null;
  demo: string | null;
  /** App Store / Play links. Leave null until real URLs exist — never '#'. */
  appStoreLink?: string | null;
  playStoreLink?: string | null;
  /** Shown next to store badges, e.g. 'US' for region-locked listings. */
  storeRegion?: string | null;
  /** Project logo / title image shown on the card. */
  image?: string | null;
  /** 'cover' fills the frame; 'contain' shows the whole logo on `imageBg`. */
  imageFit?: 'cover' | 'contain';
  imageBg?: string;
}

export interface SkillItem {
  name: string;
  color: string;
}

export interface SkillCategory {
  label: string;
  items: SkillItem[];
}

export interface ExpertiseItem {
  title: string;
  desc: string;
  icon: string;
}

export interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  current: boolean;
  desc: string[];
}

export interface VideoIntroSection {
  overline: string;
  title: string;
  subtitle: string;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  videoPlatform: 'youtube' | 'vimeo' | null;
  stats: Array<{ value: string; label: string }>;
}


// ─── Content ──────────────────────────────────────────────────────────────────

const socialLinks: SocialLinks = {
  github:   'https://github.com/sajjadHunjra71',
  linkedin: 'https://www.linkedin.com/in/sajjadkhalid753',
  email:    'sajjadkhalid753@gmail.com',
};

export const content = {
  header: {
    logo: { initial: 'S', rest: 'K' },
    navItems: [
      { label: 'Home',       to: 'home' },
      { label: 'About',      to: 'about' },
      { label: 'Expertise',  to: 'expertise' },
      { label: 'Experience', to: 'experience' },
      { label: 'Projects',   to: 'projects' },
      { label: 'Skills',     to: 'skills' },
      { label: 'Contact',    to: 'contact' },
    ] as NavItem[],
    cta: "Let's Talk",
  },

  hero: {
    roles: [
      'REACT NATIVE ENGINEER',
      'CROSS-PLATFORM iOS & ANDROID',
      'REACT.JS · NEXT.JS WEB APPS',
    ],
    headline: 'Cross-platform apps\nfor iOS, Android & web.',
    badges: ['React Native', 'Expo', 'TypeScript', 'React.js', 'Next.js'],
    description:
      'React Native Engineer with 4+ years shipping production apps for iOS and Android across fintech, payments, wellness, and logistics. I also build fast, SEO-friendly web apps with React.js and Next.js, so one engineer can cover your product on every screen.',
    buttons: {
      primary:   'View My Work',
      secondary: 'Download Resume',
    },
    socialLinks,
    stats: [
      { value: '4+',  label: 'Years shipping' },
      { value: '10+', label: 'Production apps' },
      { value: '5',   label: 'Store launches' },
    ],
    badges2: [
      { label: '4+ Years Experience' },
      { label: '10+ Production Apps' },
    ],
    image: personalImage,
  },

  about: {
    overline: 'ABOUT ME',
    title: 'Mobile-first engineer,\nfluent on the web',
    description:
      "I'm Sajjad Khalid, a React Native Engineer based in Lahore, Pakistan. For 4+ years I've built and shipped cross-platform mobile apps for iOS and Android: digital wallets, bank account linking, KYC, payments, geolocation, push notifications, and App Store / Google Play releases with React Native, TypeScript, Expo, and Redux Toolkit.\n\nI'm just as comfortable on the web. I build React.js and Next.js applications with SSR and SSG, Vite tooling, Tailwind CSS, and REST or GraphQL APIs, and I tune them for Core Web Vitals. Many of my products shipped as a mobile app and a web portal together, sharing the same API and state architecture.\n\nI've led a MobX to Redux Toolkit migration, upgraded apps from Expo SDK 46 to current versions, fixed a critical memory leak in a long-session speech app, and mentored two developers. I work in distributed Agile teams and use AI-assisted workflows to debug and refactor faster without lowering code quality.",
    stats: [
      { value: '4+',  label: 'Years Experience', sub: 'Mobile & web' },
      { value: '10+', label: 'Production Apps',  sub: 'iOS, Android & web' },
      { value: '5',   label: 'Store Launches',   sub: 'Incl. a banking app' },
    ],
    image: aboutImage,
  },

  // Not rendered until a video exists — see App.tsx.
  videoIntro: {
    overline: 'IN ACTION',
    title: 'Mobile engineering\nin production',
    subtitle: 'A walkthrough of cross-platform mobile apps, architecture decisions, and shipping to the App Store and Google Play.',
    videoUrl: null,
    thumbnailUrl: null,
    videoPlatform: null,
    stats: [
      { value: '4+',  label: 'Years Production' },
      { value: '10+', label: 'Production Apps' },
      { value: '5',   label: 'Store Launches' },
    ],
  } as VideoIntroSection,

  expertise: {
    overline: 'WHAT I BUILD',
    title: 'One engineer for mobile and web',
    items: [
      {
        title: 'Cross-Platform Mobile Apps',
        desc:  'Production React Native and Expo apps for iOS and Android: navigation, native modules, Expo SDK upgrades, and App Store / Google Play releases through GitHub Actions and Fastlane.',
        icon:  'architecture',
      },
      {
        title: 'Web Apps with React & Next.js',
        desc:  'React.js and Next.js applications with SSR and SSG, Vite builds, semantic HTML, modern CSS and Tailwind, written in TypeScript and ES6+.',
        icon:  'web',
      },
      {
        title: 'Performance & Core Web Vitals',
        desc:  'Memoization, list virtualization, bundle splitting, and memory-leak fixes on mobile. LCP, INP, and CLS tuning on the web.',
        icon:  'performance',
      },
      {
        title: 'State & Data Layers',
        desc:  'Redux Toolkit, RTK Query, TanStack Query, REST and GraphQL APIs, and WebSockets, with caching and pagination that work the same on mobile and web.',
        icon:  'state',
      },
      {
        title: 'Payments, Auth & KYC',
        desc:  'Stripe, Apple Pay, Google Pay, bank account linking, Jumio KYC, and JWT with role-based access: end-to-end fintech flows.',
        icon:  'security',
      },
      {
        title: 'Maps, Location & Real-Time',
        desc:  'Google Maps, React Native Maps, background location, geofencing, live GPS tracking, push notifications, and real-time voice with LiveKit.',
        icon:  'location',
      },
    ] as ExpertiseItem[],
  },

  experience: {
    overline: 'MY EXPERIENCE',
    title: "Where I've worked",
    items: [
      {
        role:    'React Native Engineer',
        company: 'Ascendify Technologies',
        period:  'January 2024 – Present',
        current: true,
        desc: [
          'Developed 10+ production apps using React Native, Expo, Redux Toolkit, React.js, and native iOS/Android integrations.',
          'Led the migration from MobX to Redux Toolkit, reorganizing state to improve predictability, maintainability, and debugging.',
          'Built digital payment, bank account linking, authentication, and third-party integration workflows for fintech products.',
          'Diagnosed production issues across JavaScript, React Native, and native layers, including a critical memory leak in a speech-to-text app.',
          'Upgraded 3+ apps from Expo SDK 46 to modern Expo versions, resolving dependency conflicts, native build issues, and iOS/Android compatibility.',
          'Worked in a 5-person team, mentored 2 developers, and contributed to 5 production deployments, including a banking product serving thousands of users.',
        ],
      },
      {
        role:    'Frontend Developer (Web & Mobile)',
        company: 'Qubitars Technologies',
        period:  'April 2022 – September 2023',
        current: false,
        desc: [
          'Built React.js web portals and React Native / Expo mobile apps for an e-commerce and service marketplace product.',
          'Developed scalable web applications for international clients while collaborating with distributed teams.',
          'Integrated USPS tracking APIs across web and mobile and implemented post-purchase order tracking workflows.',
          'Built map-based merchant discovery with geolocation, deep linking, and map navigation.',
          'Implemented JWT authentication and role-based access across protected areas on web and mobile.',
        ],
      },
    ] as ExperienceItem[],
  },

  projects: {
    overline: 'MY WORK',
    title: 'Selected Projects',
    subtitle: 'Production apps for iOS, Android, and the web',
    items: [
      {
        id:          1,
        title:       'Cannacard',
        category:    'mobile',
        platforms:   ['ios', 'android', 'web'],
        featured:    true,
        preview:     'wallet',
        tagline:     'Mobile payments & loyalty platform',
        description: 'A payments and loyalty platform with digital wallets, bank-funded balances, QR payments, merchant discovery, and rewards, shipped as an iOS/Android app and a companion web portal.',
        highlights: [
          'Wallet, transaction, and bank-linking flows with Jumio KYC',
          'Location-aware merchant discovery with background location',
          'CI/CD releases to both stores with GitHub Actions and Fastlane',
        ],
        tech:        ['React Native', 'Expo', 'TypeScript', 'Redux Toolkit', 'Stripe', 'Jumio', 'OneSignal', 'React.js'],
        color:       '#10B981',
        status:      'Production',
        image:       cannacardLogo,
        github:      null,
        demo:        null,
        appStoreLink: null,
        storeRegion: 'US',
      },
      {
        id:          2,
        title:       'MindHue',
        category:    'mobile',
        platforms:   ['ios', 'android', 'web'],
        featured:    true,
        preview:     'board',
        tagline:     'Wellness, vision boards & marketplace',
        description: 'A wellness platform for building vision boards, discovering curated content, and buying wellness products, with a mobile app and a marketplace web portal.',
        highlights: [
          'Vision boards, marketplace, checkout, and order management',
          'Stripe, Apple Pay, and Google Pay purchase flows',
          'Scalable state and navigation architecture',
        ],
        tech:        ['React Native', 'Expo', 'TypeScript', 'Redux Toolkit', 'Stripe', 'Firebase', 'NativeWind'],
        color:       '#F472B6',
        status:      'Production',
        image:       mindhueLogo,
        imageFit:    'contain',
        imageBg:     '#0051C0',
        github:      null,
        demo:        null,
        appStoreLink: null,
        storeRegion: 'US',
      },
      {
        id:          3,
        title:       'Traiga Driver',
        category:    'mobile',
        platforms:   ['ios', 'android'],
        preview:     'map',
        tagline:     'Last-mile delivery & live tracking',
        description: 'A driver app for real-time visibility across last-mile delivery: live GPS tracking, route visualization, task lifecycle, and digital proof of delivery.',
        highlights: [
          'Battery-optimized background geolocation',
          'Custom map markers and route polylines',
        ],
        tech:        ['React Native', 'TypeScript', 'React Native Maps', 'Background Geolocation'],
        color:       '#F59E0B',
        status:      'Production',
        image:       traigaLogo,
        imageFit:    'contain',
        imageBg:     '#FFFFFF',
        github:      null,
        demo:        null,
      },
      {
        id:          4,
        title:       'Old Glory Pay',
        category:    'web',
        platforms:   ['web', 'ios', 'android'],
        featured:    true,
        preview:     'dashboard',
        tagline:     'Digital banking portal & wallet',
        description: 'A banking web portal built from scratch, with dashboard, transaction history, KYC onboarding, and QR merchant payments, alongside a P2P wallet app.',
        highlights: [
          'Real-time transaction APIs and push notifications',
          'Wallet payment latency cut by 25%',
        ],
        tech:        ['React.js', 'TypeScript', 'Tailwind CSS', 'Redux Toolkit', 'Jumio KYC', 'OneSignal'],
        color:       '#38BDF8',
        status:      'Production',
        image:       oldGloryPayLogo,
        github:      null,
        demo:        null,
      },
      {
        id:          5,
        title:       'Connect AI',
        category:    'mobile',
        platforms:   ['ios', 'android'],
        preview:     'voice',
        tagline:     'Real-time AI voice coaching',
        description: 'A mobile app that combines real-time AI voice sessions with interactive coaching, built for long, performance-sensitive sessions.',
        highlights: [
          'Real-time voice sessions with LiveKit',
          'Notification and re-engagement flows',
        ],
        tech:        ['React Native', 'Expo', 'TypeScript', 'Redux Toolkit', 'LiveKit'],
        color:       '#818CF8',
        status:      'Production',
        github:      null,
        demo:        null,
      },
      {
        id:          6,
        title:       'Smodin',
        category:    'web',
        platforms:   ['web'],
        preview:     'editor',
        tagline:     'AI writing platform used by millions',
        description: 'Frontend feature work on an AI content creation and rewriting tool used by millions of people, building fast, reusable React UI.',
        highlights: [
          'Reusable, performant React components',
        ],
        tech:        ['React.js', 'TypeScript', 'REST APIs'],
        color:       '#A78BFA',
        status:      'Production',
        github:      null,
        demo:        null,
      },
      {
        id:          7,
        title:       'Taxi Link Brighton',
        category:    'web',
        platforms:   ['web'],
        preview:     'booking',
        tagline:     'Taxi booking with real-time availability',
        description: 'A taxi booking and management platform with real-time availability, booking flows, and a responsive interface.',
        highlights: [
          'Responsive booking flow on every screen size',
        ],
        tech:        ['React.js', 'TypeScript', 'Tailwind CSS'],
        color:       '#FB923C',
        status:      'Production',
        image:       taxiLinkLogo,
        imageFit:    'contain',
        imageBg:     '#111827',
        github:      null,
        demo:        'https://www.taxilinkbrighton.co.uk/',
      },
    ] as ProjectItem[],
  },

  skills: {
    overline: 'TECHNICAL STACK',
    title: 'Mobile first. Web fluent.',
    categories: [
      {
        label: 'Mobile Development',
        items: [
          { name: 'React Native',             color: '#61DAFB' },
          { name: 'Expo',                     color: '#4630EB' },
          { name: 'React Navigation',         color: '#764ABC' },
          { name: 'Native Modules',           color: '#34A853' },
          { name: 'iOS / Xcode',              color: '#A2AAAD' },
          { name: 'Android / Android Studio', color: '#3DDC84' },
          { name: 'NativeWind',               color: '#38BDF8' },
          { name: 'Gluestack UI',             color: '#818CF8' },
          { name: 'App Store & Google Play',  color: '#0D9488' },
          { name: 'Fastlane',                 color: '#0D9488' },
          { name: 'Push Notifications',       color: '#E54B4D' },
        ],
      },
      {
        label: 'Web Development',
        items: [
          { name: 'React.js',          color: '#61DAFB' },
          { name: 'Next.js',           color: '#94A3B8' },
          { name: 'SSR / SSG',         color: '#94A3B8' },
          { name: 'Vite',              color: '#A855F7' },
          { name: 'JavaScript (ES6+)', color: '#F7DF1E' },
          { name: 'TypeScript',        color: '#3178C6' },
          { name: 'HTML5',             color: '#E34F26' },
          { name: 'CSS3',              color: '#1572B6' },
          { name: 'Tailwind CSS',      color: '#38BDF8' },
          { name: 'MUI',               color: '#007FFF' },
          { name: 'Responsive Design', color: '#10B981' },
          { name: 'Core Web Vitals',   color: '#34D399' },
        ],
      },
      {
        label: 'State & Data',
        items: [
          { name: 'Redux Toolkit',  color: '#764ABC' },
          { name: 'RTK Query',      color: '#764ABC' },
          { name: 'TanStack Query', color: '#FF4154' },
          { name: 'Context API',    color: '#61DAFB' },
          { name: 'REST APIs',      color: '#FF6C37' },
          { name: 'GraphQL',        color: '#E10098' },
          { name: 'WebSockets',     color: '#10B981' },
        ],
      },
      {
        label: 'Payments & Integrations',
        items: [
          { name: 'Stripe',               color: '#635BFF' },
          { name: 'Apple Pay',            color: '#A2AAAD' },
          { name: 'Google Pay',           color: '#4285F4' },
          { name: 'Bank Account Linking', color: '#0D9488' },
          { name: 'Jumio KYC',            color: '#00A0DC' },
          { name: '1Link',                color: '#0D9488' },
          { name: 'Firebase',             color: '#FFCA28' },
          { name: 'OneSignal',            color: '#E54B4D' },
          { name: 'Twilio',               color: '#F22F46' },
          { name: 'SendGrid',             color: '#1A82E2' },
        ],
      },
      {
        label: 'Location & Real-Time',
        items: [
          { name: 'React Native Maps',   color: '#EA4335' },
          { name: 'Google Maps SDK',     color: '#34A853' },
          { name: 'Background Location', color: '#0D9488' },
          { name: 'Geofencing',          color: '#7C3AED' },
          { name: 'Reverse Geocoding',   color: '#059669' },
          { name: 'LiveKit',             color: '#818CF8' },
        ],
      },
      {
        label: 'Testing & Tooling',
        items: [
          { name: 'Jest',                         color: '#C21325' },
          { name: 'React Native Testing Library', color: '#E33332' },
          { name: 'Sentry',                       color: '#8B7FD1' },
          { name: 'GitHub Actions',               color: '#2088FF' },
          { name: 'Docker',                       color: '#2496ED' },
          { name: 'Git',                          color: '#F05032' },
          { name: 'Agile / Scrum',                color: '#10B981' },
        ],
      },
    ] as SkillCategory[],
  },

  contact: {
    overline: 'GET IN TOUCH',
    title: "Let's work together",
    description: "Need someone to build or scale a mobile app, or to ship the web app that goes with it? I'm open to full-time remote roles and contract work with React Native, React.js, and Next.js.",
    info: {
      phone:    { label: 'Phone',    value: '+92 300 7449748' },
      email:    { label: 'Email',    value: 'sajjadkhalid753@gmail.com' },
      location: { label: 'Location', value: 'Lahore, Pakistan' },
    },
    form: {
      name:    'Your Name',
      email:   'Your Email',
      subject: 'Subject',
      message: 'Message',
      btn:     'Send Message',
      note:    "I'll reply within 24 hours.",
    },
  },

  footer: {
    copyright: `© ${new Date().getFullYear()} Sajjad Khalid. All rights reserved.`,
    navItems: [
      { label: 'About',      to: 'about' },
      { label: 'Expertise',  to: 'expertise' },
      { label: 'Experience', to: 'experience' },
      { label: 'Projects',   to: 'projects' },
      { label: 'Contact',    to: 'contact' },
    ] as NavItem[],
    socialLinks,
  },
};
